import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { getName } from 'utils'
import { useTranslation } from 'react-i18next'
import { Collapse } from 'reactstrap'
import { isEmpty } from 'lodash'
import CategoryItem from './CategoryItem'

const StyledAccordionDropdownPanelWrapper = styled.div`
  max-height: 382px;
  background-color: #fff;
  box-shadow: 0px 0px 16px 0px #363e511a;
  box-shadow: 0px 1px 4px 0px #363e5133;
  border-radius: 6px;

  label {
    margin-bottom: 0;
  }
`

const StyledCategoryBlock = styled.div`
  border-bottom: solid 1px #d3dde6;
`

const StyledCategoriesBlock = styled.div`
  overflow-y: scroll;
  max-height: calc(382px - 45px);
`

const AccordionDropdownPanel = ({ onCategoryChange, categories }) => {
  const { t, i18n } = useTranslation()
  const [categoryOpenedIds, setCategoryOpenedIds] = useState({})
  const [selectedCategoryIds, setSelectedCategoryIds] = useState({})
  const [isShowCheckedAll, setIsShowCheckedAll] = useState(true)
  const isSelectedAll = Object.entries(selectedCategoryIds).filter(([_, value]) => value).length === 0

  const categoriesMapping = useMemo(() => {
    const result = {}

    categories.forEach(({ id: firstCategoryId, children: firstCategoryChildren }) => {
      // layer 1
      result[firstCategoryId] = {
        parent: [],
        children: [],
      }

      firstCategoryChildren &&
        firstCategoryChildren.forEach(({ id: secondCategoryId, children: secondCategoryChildren }) => {
          // layer 2

          result[secondCategoryId] = {
            parent: [firstCategoryId],
            children: [],
          }

          if (!secondCategoryChildren) {
            result[firstCategoryId].children.push(secondCategoryId)
          }

          secondCategoryChildren &&
            secondCategoryChildren.forEach(({ id: thirdCategoryId }) => {
              // layer 3
              result[firstCategoryId].children.push(thirdCategoryId)
              result[secondCategoryId].children.push(thirdCategoryId)

              result[thirdCategoryId] = {
                parent: [firstCategoryId, secondCategoryId],
                children: [],
              }
            })
        })
    })

    return result
  }, [categories])

  const handleCategoryOpenedIds = (id) => {
    setCategoryOpenedIds({ ...categoryOpenedIds, [id]: !categoryOpenedIds[id] })
  }

  const handleSelectedCategoryIds = (category) => {
    const { isInterminate, checked } = isCheckedOrInterminate(category)
    const checkStatus = !(checked && !isInterminate)

    const nextSelectedCategoryIds = {
      ...selectedCategoryIds,
      [category.id]: checkStatus,
    }

    categoriesMapping[category.id].children.forEach((categoryId) => {
      nextSelectedCategoryIds[categoryId] = checkStatus
    })

    if (!checkStatus) {
      categoriesMapping[category.id].parent.forEach((categoryId) => {
        nextSelectedCategoryIds[categoryId] = false
      })
    }

    setSelectedCategoryIds(nextSelectedCategoryIds)
    setIsShowCheckedAll(false)
    onCategoryChange(
      Object.entries(nextSelectedCategoryIds)
        .filter(([_, value]) => value)
        .map(([key]) => key),
    )
  }

  const isCheckedOrInterminate = (category) => {
    let isInterminate = false
    let checked = !!selectedCategoryIds[category.id]

    const allChildrenCategoriesId = categoriesMapping[category.id].children
    const allSelectedCategoriesId = Object.entries(selectedCategoryIds)
      .filter(([_, value]) => value)
      .map(([key]) => key)

    if (allSelectedCategoriesId.length === 0 || allChildrenCategoriesId.length == 0) {
      return {
        isInterminate,
        checked,
      }
    }

    const commonValues = allChildrenCategoriesId.filter((value) => allSelectedCategoriesId.includes(value))

    if (commonValues.length === allChildrenCategoriesId.length) {
      // 完全相同
      isInterminate = false
      checked = true
    } else if (commonValues.length > 0) {
      // 部分相同
      isInterminate = true
      checked = true
    }

    return {
      isInterminate,
      checked,
    }
  }

  const handleSelectedAllClick = () => {
    const hasSelectedItems = Object.values(selectedCategoryIds).some((value) => value)
    if (isSelectedAll) {
      setIsShowCheckedAll(!isShowCheckedAll)
    }

    if (hasSelectedItems) {
      setIsShowCheckedAll(true)
    }
    setSelectedCategoryIds({})
    onCategoryChange([])
  }

  if (isEmpty(categoriesMapping)) {
    return null
  }

  return (
    <StyledAccordionDropdownPanelWrapper>
      <StyledCategoryBlock>
        <CategoryItem
          layer={1}
          label={t('allCategory')}
          onClick={handleSelectedAllClick}
          checked={isSelectedAll && isShowCheckedAll}
        />
      </StyledCategoryBlock>

      <StyledCategoriesBlock>
        {/* Layer 1 */}
        {categories.map((category) => {
          const { isInterminate, checked: categoryChecked } = isCheckedOrInterminate(category)
          return (
            <div key={category.id}>
              <CategoryItem
                layer={1}
                label={getName({ obj: category.name_translations, i18n: i18n.language })}
                isShowChevron={category?.children?.length > 0}
                isOpen={categoryOpenedIds[category.id]}
                onChevronClick={() => handleCategoryOpenedIds(category.id)}
                onClick={() => handleSelectedCategoryIds(category)}
                checked={selectedCategoryIds[category.id] || categoryChecked || (isSelectedAll && isShowCheckedAll)}
                interminate={isInterminate && !selectedCategoryIds[category.id]}
              />

              {/* Layer 2 */}
              {category?.children?.length > 0 && (
                <Collapse isOpen={categoryOpenedIds[category.id]}>
                  {category.children.map((secondCategory) => {
                    const { isInterminate: secondCategoryInterminate, checked: secondCategoryChecked } =
                      isCheckedOrInterminate(secondCategory)
                    return (
                      <div key={secondCategory.id}>
                        <CategoryItem
                          layer={2}
                          isOpen={categoryOpenedIds[secondCategory.id]}
                          label={getName({ obj: secondCategory.name_translations, i18n: i18n.language })}
                          isShowChevron={secondCategory?.children?.length > 0}
                          onChevronClick={() => handleCategoryOpenedIds(secondCategory.id)}
                          onClick={() => handleSelectedCategoryIds(secondCategory)}
                          checked={
                            selectedCategoryIds[category.id] ||
                            selectedCategoryIds[secondCategory.id] ||
                            secondCategoryChecked ||
                            (isSelectedAll && isShowCheckedAll)
                          }
                          interminate={secondCategoryInterminate && !selectedCategoryIds[secondCategory.id]}
                        />

                        {/* Layer 3 */}
                        {secondCategory?.children?.length > 0 && (
                          <Collapse isOpen={categoryOpenedIds[secondCategory.id]}>
                            {secondCategory.children.map((thirdCategory) => (
                              <CategoryItem
                                key={thirdCategory.id}
                                layer={3}
                                label={getName({ obj: thirdCategory.name_translations, i18n: i18n.language })}
                                checked={
                                  selectedCategoryIds[category.id] ||
                                  selectedCategoryIds[secondCategory.id] ||
                                  selectedCategoryIds[thirdCategory.id] ||
                                  (isSelectedAll && isShowCheckedAll)
                                }
                                onClick={() => handleSelectedCategoryIds(thirdCategory)}
                              />
                            ))}
                          </Collapse>
                        )}
                      </div>
                    )
                  })}
                </Collapse>
              )}
            </div>
          )
        })}
      </StyledCategoriesBlock>
    </StyledAccordionDropdownPanelWrapper>
  )
}

AccordionDropdownPanel.propTypes = {
  onCategoryChange: PropTypes.func.isRequired,
  categories: PropTypes.array.isRequired,
}

AccordionDropdownPanel.defaultProps = {
  onCategoryChange: () => {},
  categories: [],
}

export default AccordionDropdownPanel

/* 
  * DOM tree
  <CategoryItem label="分類第一層" isShowChevron />
  <Collapse isOpen={isOpen}>
    <CategoryItem label="分類二層-1" isShowChevron checked layer={2} />

    <Collapse isOpen>
      <CategoryItem label="分類三層-1" layer={3} checked />
      <CategoryItem label="分類三層-2" layer={3} checked />
    </Collapse>

    <CategoryItem label="分類二層-2" layer={2} isShowChevron checked />
    <Collapse isOpen>
      <CategoryItem label="分類三層-1" layer={3} checked />
      <CategoryItem label="分類三層-2" layer={3} checked />
    </Collapse>
  </Collapse> 
*/
